import { TILE_HEIGHT, TILE_WIDTH } from '@/shared/constants';

export type ProceduralTownBuilding = {
  offsetX: number;
  offsetY: number;
  variant: number;
  scale: number;
};

export type ProceduralTownProp = {
  objectType: 'cart' | 'barrel' | 'wagon_wheel' | 'bench';
  variant: number;
  offsetX: number;
  offsetY: number;
  footprintW: number;
  footprintH: number;
};

const BUILDING_COUNTS: Record<string, number> = { XS: 5, S: 7, M: 10, L: 14, XL: 14 };
const BLOCK_COLUMNS = [-0.92, -0.48, 0.48, 0.92] as const;
const BLOCK_ROWS = [-0.48, -0.16, 0.16, 0.48] as const;
const PROP_FOOTPRINTS = {
  cart: { footprintW: 2.1, footprintH: 1.3 },
  barrel: { footprintW: 0.8, footprintH: 0.8 },
  wagon_wheel: { footprintW: 1, footprintH: 1 },
  bench: { footprintW: 1.5, footprintH: 0.8 },
} as const;

/** True when two axis-aligned ground footprints overlap or leave less than the requested gap. */
export function footprintsOverlap(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
  clearance = 0,
): boolean {
  return Math.abs(a.x - b.x) < (a.width + b.width) / 2 + clearance
    && Math.abs(a.y - b.y) < (a.height + b.height) / 2 + clearance;
}

function screenOffsetToWorld(x: number, y: number): { x: number; y: number } {
  return {
    x: (x / (TILE_WIDTH / 2) + y / (TILE_HEIGHT / 2)) / 2,
    y: (y / (TILE_HEIGHT / 2) - x / (TILE_WIDTH / 2)) / 2,
  };
}

function buildingFootprint(building: ProceduralTownBuilding) {
  return { x: building.offsetX, y: building.offsetY, width: 0.9, height: 0.75 };
}

function propVariant(seed: number, index: number): number {
  return (((seed * 41 + index * 19) ^ (index * 0x9e3779b9)) >>> 0) % 5;
}

function prop(
  objectType: ProceduralTownProp['objectType'],
  variant: number,
  offsetX: number,
  offsetY: number,
): ProceduralTownProp {
  return { objectType, variant, offsetX, offsetY, ...PROP_FOOTPRINTS[objectType] };
}

function placementIsClear(
  candidate: ProceduralTownProp,
  placed: ProceduralTownProp[],
  buildings: Array<{ x: number; y: number; width: number; height: number }>,
): boolean {
  const footprint = { x: candidate.offsetX, y: candidate.offsetY, width: candidate.footprintW, height: candidate.footprintH };
  return !buildings.some((building) => footprintsOverlap(footprint, building, 0.25))
    && !placed.some((other) => footprintsOverlap(footprint, {
      x: other.offsetX, y: other.offsetY, width: other.footprintW, height: other.footprintH,
    }, 0.25));
}

/** Stable block layout with a clear center avenue and validated building ground footprints. */
export function proceduralTownBuildings(
  townSize: string,
  townSeed: number,
  townRadius: number,
): ProceduralTownBuilding[] {
  const count = BUILDING_COUNTS[townSize] ?? BUILDING_COUNTS.M!;
  const spread = Math.max(1, townRadius * TILE_WIDTH * 0.4);
  const scale = Math.max(0.22, Math.min(0.58, townRadius * 0.045));
  const slots: Array<{ col: number; row: number }> = [];
  for (let row = 0; row < BLOCK_ROWS.length; row++) {
    // Outer then inner lots on each side keep the avenue open at x=0.
    for (const col of [0, 3, 1, 2]) slots.push({ col, row });
  }

  const result: ProceduralTownBuilding[] = [];
  for (let i = 0; i < count; i++) {
    const slot = slots[i]!;
    const hash = ((townSeed * 31 + i * 17) ^ (i * 0x45d9f3b)) >>> 0;
    const jitterX = (((hash & 0xff) / 255) - 0.5) * spread * 0.015;
    const jitterY = ((((hash >> 8) & 0xff) / 255) - 0.5) * spread * 0.015;
    const offset = screenOffsetToWorld(
      BLOCK_COLUMNS[slot.col]! * spread + jitterX,
      BLOCK_ROWS[slot.row]! * spread + jitterY,
    );
    const candidate: ProceduralTownBuilding = {
      offsetX: offset.x,
      offsetY: offset.y,
      variant: hash % 5,
      scale,
    };
    const footprint = buildingFootprint(candidate);
    if (!result.some((placed) => footprintsOverlap(footprint, buildingFootprint(placed), 0.1))) {
      result.push(candidate);
    }
  }
  return result;
}

/** Default city props used only when no saved town_objects rows exist. */
export function proceduralTownProps(townSize: string, townSeed: number, townRadius: number): ProceduralTownProp[] {
  const buildings = proceduralTownBuildings(townSize, townSeed, townRadius).map(buildingFootprint);
  const radius = Math.max(3, townRadius);
  const placed: ProceduralTownProp[] = [];
  const angles = [0, Math.PI / 2, Math.PI, Math.PI * 1.5].map((angle, i) => angle + ((townSeed % 7) - 3) * 0.025 + i * 0.001);
  const cartAnchor = angles.map((angle) => ({ x: Math.cos(angle) * radius * 0.84, y: Math.sin(angle) * radius * 0.84 }))
    .find((anchor) => {
      const group = [
        prop('cart', 0, anchor.x, anchor.y),
        prop('barrel', 1, anchor.x + 2, anchor.y),
        prop('wagon_wheel', 2, anchor.x - 2, anchor.y + 1.5),
      ];
      return group.every((item, index) => placementIsClear(item, group.slice(0, index), buildings));
    });

  if (cartAnchor) {
    placed.push(prop('cart', propVariant(townSeed, 0), cartAnchor.x, cartAnchor.y));
    placed.push(prop('barrel', propVariant(townSeed, 1), cartAnchor.x + 2, cartAnchor.y));
    placed.push(prop('wagon_wheel', propVariant(townSeed, 2), cartAnchor.x - 2, cartAnchor.y + 1.5));
  }

  if (townSize === 'L' || townSize === 'XL') {
    const benchOffsets = [
      { x: radius * 0.24, y: 0 }, { x: -radius * 0.24, y: 0 },
      { x: 0, y: radius * 0.24 }, { x: 0, y: -radius * 0.24 },
    ];
    const bench = benchOffsets.find((offset) => placementIsClear(
      prop('bench', propVariant(townSeed, 3), offset.x, offset.y), placed, buildings,
    ));
    if (bench) placed.push(prop('bench', propVariant(townSeed, 3), bench.x, bench.y));
  }
  return placed;
}

/** Compact deterministic yard props around a single roadside inn/tavern building. */
export function proceduralRoadsidePOIProps(
  seed: number,
  radius: number,
  anchorOffsetX: number,
  anchorOffsetY: number,
  awayX: number,
  awayY: number,
  tangentX: number,
  tangentY: number,
): ProceduralTownProp[] {
  const awayLength = Math.hypot(awayX, awayY) || 1;
  const away = { x: awayX / awayLength, y: awayY / awayLength };
  let tangentLength = Math.hypot(tangentX, tangentY);
  const tangent = tangentLength > 0
    ? { x: tangentX / tangentLength, y: tangentY / tangentLength }
    : { x: -away.y, y: away.x };
  tangentLength = Math.hypot(tangent.x, tangent.y) || 1;
  tangent.x /= tangentLength;
  tangent.y /= tangentLength;
  const reach = Math.max(2.35, Math.min(3.2, radius * 0.55));
  const cartX = anchorOffsetX + away.x * reach;
  const cartY = anchorOffsetY + away.y * reach;
  const buildings = [{ x: anchorOffsetX, y: anchorOffsetY, width: 2.5, height: 2 }];
  const result: ProceduralTownProp[] = [];
  const add = (item: ProceduralTownProp) => {
    if (placementIsClear(item, result, buildings)) result.push(item);
  };
  add(prop('cart', propVariant(seed, 0), cartX, cartY));
  add(prop('barrel', propVariant(seed, 1), cartX + tangent.x * 2.2, cartY + tangent.y * 2.2));
  add(prop('wagon_wheel', propVariant(seed, 2), cartX - tangent.x * 2.2, cartY - tangent.y * 2.2));
  add(prop('bench', propVariant(seed, 3), anchorOffsetX + tangent.x * 3, anchorOffsetY + tangent.y * 3));
  return result;
}
